import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { supabase } from './supabaseClient'

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const SERVICES = [
  'ABA Therapy – Individual Session', 'ABA Therapy – Group Session',
  'Parent Training', 'Functional Behavior Assessment',
  'Initial Intake / Evaluation', 'Treatment Plan Development',
  'Progress Monitoring & Data Review', 'Social Skills Training',
  'Discrete Trial Training (DTT)', 'Natural Environment Teaching (NET)',
  'Crisis Intervention', 'Telehealth Session',
]

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const msToH    = ms => ms / 3600000
const fmtH     = ms => msToH(ms).toFixed(2)
const fmtDur   = ms => {
  const s = Math.floor(ms / 1000), h = Math.floor(s / 3600),
        m = Math.floor((s % 3600) / 60), sec = s % 60
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`
}
const fmtTime  = d => d ? new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '—'
const fmtDate  = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'
const fmtDT    = d => d ? new Date(d).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
const fmtMoney = n => `$${Number(n).toFixed(2)}`
const wkStart  = d => { const s = new Date(d); s.setDate(d.getDate() - d.getDay()); s.setHours(0, 0, 0, 0); return s }
const wkEnd    = d => { const e = new Date(wkStart(d)); e.setDate(e.getDate() + 6); e.setHours(23, 59, 59, 999); return e }
const within24h = d => d && (Date.now() - new Date(d).getTime()) < 86_400_000
const toLocalDT = d => {
  if (!d) return ''
  const dt = new Date(d), pad = n => String(n).padStart(2, '0')
  return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`
}

function sessToCSV(rows, profiles = []) {
  const hdr = ['Date','Day','Staff','Client','Service','Clock In','Clock Out','Hours','Corrected','Lat','Lng','Notes']
  const body = rows.map(s => {
    const u = profiles.find(p => p.id === s.user_id)
    const dur = s.duration_ms || (s.clock_out ? new Date(s.clock_out) - new Date(s.clock_in) : 0)
    return [
      fmtDate(s.clock_in),
      new Date(s.clock_in).toLocaleDateString('en-US', { weekday: 'long' }),
      u?.name || '—', s.client_name, s.service,
      fmtDT(s.clock_in), fmtDT(s.clock_out), fmtH(dur),
      s.corrected ? 'Yes' : 'No', s.lat || '', s.lng || '',
      `"${(s.notes || '').replace(/"/g, '""')}"`
    ]
  })
  return [hdr, ...body].map(r => r.join(',')).join('\n')
}
function dlCSV(csv, name) {
  const b = new Blob([csv], { type: 'text/csv' }), u = URL.createObjectURL(b), a = document.createElement('a')
  a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u)
}

// ─────────────────────────────────────────────────────────────────────────────
// DESIGN TOKENS
// ─────────────────────────────────────────────────────────────────────────────
const T = {
  teal: '#00B5C8', dteal: '#007A8A', pink: '#E91E8C', yellow: '#FFD600',
  green: '#00C864', orange: '#FB923C', purple: '#A78BFA',
  bg: '#090f1e', bg2: '#0b1a35', card: 'rgba(255,255,255,0.032)',
  border: 'rgba(255,255,255,0.08)', dim: 'rgba(255,255,255,0.38)',
}
const ACCENTS = [T.teal, T.pink, T.yellow, T.purple, T.green, T.orange, '#60A5FA', '#F472B6', '#34D399', '#FBBF24']

// ─────────────────────────────────────────────────────────────────────────────
// UI PRIMITIVES
// ─────────────────────────────────────────────────────────────────────────────
const Card = ({ children, style = {} }) => (
  <div style={{ background: T.card, borderRadius: 18, padding: 22, border: `1px solid ${T.border}`, marginBottom: 16, ...style }}>
    {children}
  </div>
)
const SH = ({ children, color = T.teal }) => (
  <div style={{ fontSize: 10, letterSpacing: 2, color, marginBottom: 14, textTransform: 'uppercase', fontWeight: 'bold' }}>{children}</div>
)
const Lbl = ({ children }) => (
  <label style={{ display: 'block', fontSize: 10, letterSpacing: '1.5px', color: T.dim, marginBottom: 6, textTransform: 'uppercase' }}>{children}</label>
)
const baseInput = { width: '100%', padding: '10px 13px', borderRadius: 10, border: `1px solid ${T.border}`, color: '#f0f4f8', fontSize: 13, outline: 'none', fontFamily: 'Georgia,serif', boxSizing: 'border-box' }
const Inp = ({ dis = false, style = {}, ...p }) => (
  <input disabled={dis} {...p} style={{ ...baseInput, background: dis ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.07)', cursor: dis ? 'not-allowed' : 'text', ...style }} />
)
const Sel = ({ dis = false, style = {}, children, ...p }) => (
  <select disabled={dis} {...p} style={{ ...baseInput, background: dis ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.07)', appearance: 'none', backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Cpath fill='%2300B5C8' d='M5 7L0 2h10z'/%3E%3C/svg%3E\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: 10, cursor: dis ? 'not-allowed' : 'pointer', ...style }}>
    {children}
  </select>
)
const Btn = ({ children, variant = 'primary', onClick, style = {}, disabled = false, loading = false }) => {
  const v = {
    primary: { background: `linear-gradient(135deg,${T.teal},${T.dteal})`, boxShadow: `0 6px 20px rgba(0,181,200,0.28)` },
    danger:  { background: `linear-gradient(135deg,${T.pink},#B0005E)`,    boxShadow: `0 6px 20px rgba(233,30,140,0.25)` },
    ghost:   { background: 'rgba(255,255,255,0.06)', boxShadow: 'none',    border: `1px solid ${T.border}` },
    yellow:  { background: `linear-gradient(135deg,${T.yellow},#E6B800)`,  boxShadow: `0 6px 20px rgba(255,215,0,0.2)`,  color: '#0b1a35' },
    success: { background: `linear-gradient(135deg,${T.green},#00A050)`,   boxShadow: `0 6px 20px rgba(0,200,100,0.22)` },
  }
  return (
    <button onClick={onClick} disabled={disabled || loading}
      style={{ padding: '10px 20px', border: 'none', borderRadius: 10, color: v[variant]?.color || '#fff', fontSize: 12, fontWeight: 'bold', fontFamily: 'Georgia,serif', cursor: (disabled || loading) ? 'not-allowed' : 'pointer', letterSpacing: '0.5px', opacity: (disabled || loading) ? 0.55 : 1, display: 'flex', alignItems: 'center', gap: 7, ...v[variant], ...style }}>
      {loading ? '⏳' : children}
    </button>
  )
}
const Stat = ({ label, value, icon, accent = T.teal }) => (
  <div style={{ flex: 1, minWidth: 100, background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: '15px 13px', border: `1px solid ${accent}22` }}>
    <div style={{ fontSize: 18, marginBottom: 4 }}>{icon}</div>
    <div style={{ fontSize: 19, fontWeight: 'bold', color: accent, fontFamily: 'monospace' }}>{value}</div>
    <div style={{ fontSize: 10, color: T.dim, letterSpacing: 1, textTransform: 'uppercase', marginTop: 2 }}>{label}</div>
  </div>
)
const Badge = ({ children, color = T.teal }) => (
  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 10, fontWeight: 'bold', letterSpacing: '0.5px', background: `${color}20`, color, border: `1px solid ${color}40` }}>{children}</span>
)
const Modal = ({ open, onClose, title, children, width = '520px' }) => {
  if (!open) return null
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(8px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: '#0d1f3c', border: `1px solid ${T.teal}40`, borderRadius: 20, padding: 28, width: '100%', maxWidth: width, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 'bold', color: '#f0f4f8' }}>{title}</div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 8, width: 30, height: 30, color: T.dim, cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        {children}
      </div>
    </div>
  )
}
const Toast = ({ msg, type = 'success' }) => {
  if (!msg) return null
  return (
    <div style={{ position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)', background: type === 'error' ? `linear-gradient(135deg,${T.pink},#B0005E)` : `linear-gradient(135deg,${T.teal},${T.dteal})`, color: '#fff', padding: '11px 24px', borderRadius: 30, fontSize: 13, fontWeight: 'bold', zIndex: 9999, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', pointerEvents: 'none', whiteSpace: 'nowrap' }}>
      {type === 'error' ? '⚠ ' : '✓ '}{msg}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// LOGIN
// ─────────────────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail]     = useState('')
  const [pass, setPass]       = useState('')
  const [err, setErr]         = useState('')
  const [loading, setLoading] = useState(false)
  const [showPw, setShowPw]   = useState(false)
  const [mode, setMode]       = useState('login') // login | reset

  const submit = async () => {
    setErr(''); setLoading(true)
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password: pass })
    setLoading(false)
    if (error) { setErr(error.message); return }
    onLogin(data.user)
  }

  const sendReset = async () => {
    if (!email) { setErr('Enter your email first.'); return }
    setLoading(true)
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim())
    setLoading(false)
    if (error) { setErr(error.message); return }
    setErr(''); setMode('login')
    alert('Password reset email sent! Check your inbox.')
  }

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(135deg,${T.bg} 0%,${T.bg2} 50%,${T.bg} 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Georgia,serif', padding: 20, position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: -80, right: -80, width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle,rgba(0,181,200,0.1) 0%,transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ width: '100%', maxWidth: 400 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ width: 64, height: 64, background: `linear-gradient(135deg,${T.teal},${T.pink})`, borderRadius: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, margin: '0 auto 16px', boxShadow: `0 8px 24px rgba(0,181,200,0.35)` }}>💛</div>
          <div style={{ fontSize: 22, fontWeight: 'bold', color: '#f0f4f8' }}>HappyHearts Haven</div>
          <div style={{ fontSize: 11, color: T.dim, letterSpacing: 2, textTransform: 'uppercase', marginTop: 5 }}>Staff Time Tracker</div>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 20, padding: 28, border: `1px solid ${T.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 'bold', color: '#f0f4f8', marginBottom: 20 }}>{mode === 'login' ? 'Sign In' : 'Reset Password'}</div>
          {err && <div style={{ background: 'rgba(233,30,140,0.12)', border: `1px solid rgba(233,30,140,0.3)`, borderRadius: 9, padding: '9px 13px', marginBottom: 14, fontSize: 12, color: '#ff6eb0' }}>⚠ {err}</div>}
          <div style={{ marginBottom: 14 }}>
            <Lbl>Email Address</Lbl>
            <Inp type="email" value={email} onChange={e => { setEmail(e.target.value); setErr('') }} placeholder="you@happyheartshaven.org" onKeyDown={e => e.key === 'Enter' && mode === 'login' && submit()} />
          </div>
          {mode === 'login' && (
            <div style={{ marginBottom: 20 }}>
              <Lbl>Password</Lbl>
              <div style={{ position: 'relative' }}>
                <Inp type={showPw ? 'text' : 'password'} value={pass} onChange={e => { setPass(e.target.value); setErr('') }} placeholder="••••••••" onKeyDown={e => e.key === 'Enter' && submit()} style={{ paddingRight: 40 }} />
                <button onClick={() => setShowPw(p => !p)} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: T.dim, cursor: 'pointer', fontSize: 14 }}>{showPw ? '🙈' : '👁'}</button>
              </div>
            </div>
          )}
          {mode === 'login'
            ? <Btn variant="primary" onClick={submit} loading={loading} style={{ width: '100%', justifyContent: 'center', padding: 12 }}>Sign In →</Btn>
            : <Btn variant="primary" onClick={sendReset} loading={loading} style={{ width: '100%', justifyContent: 'center', padding: 12 }}>Send Reset Email</Btn>
          }
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <button onClick={() => { setMode(m => m === 'login' ? 'reset' : 'login'); setErr('') }} style={{ background: 'none', border: 'none', color: T.teal, cursor: 'pointer', fontSize: 12, fontFamily: 'Georgia,serif' }}>
              {mode === 'login' ? 'Forgot password?' : '← Back to sign in'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN — USER MANAGEMENT
// ─────────────────────────────────────────────────────────────────────────────
function AdminUsers({ profiles, clients, onRefresh, showToast }) {
  const [modal, setModal]     = useState(null)  // null | 'add' | profile-obj
  const [form, setForm]       = useState({})
  const [formErr, setFormErr] = useState('')
  const [saving, setSaving]   = useState(false)

  const empty = { name: '', email: '', password: '', role: 'staff', hourly_rate: 25, clients: [], active: true }

  const openAdd  = () => { setForm(empty); setFormErr(''); setModal('add') }
  const openEdit = p  => { setForm({ ...p, password: '', clients: p.clients?.map(c => c.client_name) || [] }); setFormErr(''); setModal(p) }

  const save = async () => {
    if (!form.name?.trim())  { setFormErr('Name is required.'); return }
    if (!form.email?.trim()) { setFormErr('Valid email required.'); return }
    if (modal === 'add' && !form.password) { setFormErr('Password is required.'); return }
    if (form.password && form.password.length < 6) { setFormErr('Password must be ≥6 characters.'); return }
    setSaving(true)

    if (modal === 'add') {
      // Create user via Supabase Auth (admin API)
      const { data, error } = await supabase.auth.admin.createUser({
        email: form.email.trim().toLowerCase(),
        password: form.password,
        email_confirm: true,
      })
      if (error) { setFormErr(error.message); setSaving(false); return }

      const uid = data.user.id
      // Upsert profile
      await supabase.from('profiles').upsert({ id: uid, name: form.name.trim(), role: form.role, hourly_rate: form.hourly_rate, active: true })
      // Set client assignments
      await supabase.from('client_assignments').delete().eq('user_id', uid)
      if (form.clients.length) {
        await supabase.from('client_assignments').insert(form.clients.map(c => ({ user_id: uid, client_name: c })))
      }
      showToast('User created!')
    } else {
      // Update profile
      const updates = { name: form.name.trim(), role: form.role, hourly_rate: form.hourly_rate, active: form.active }
      await supabase.from('profiles').update(updates).eq('id', modal.id)
      // Reset password if provided
      if (form.password) {
        await supabase.auth.admin.updateUserById(modal.id, { password: form.password })
      }
      // Update assignments
      await supabase.from('client_assignments').delete().eq('user_id', modal.id)
      if (form.clients.length) {
        await supabase.from('client_assignments').insert(form.clients.map(c => ({ user_id: modal.id, client_name: c })))
      }
      showToast('User updated!')
    }
    setSaving(false); setModal(null); onRefresh()
  }

  const toggleActive = async p => {
    await supabase.from('profiles').update({ active: !p.active }).eq('id', p.id)
    showToast(p.active ? 'User deactivated' : 'User reactivated')
    onRefresh()
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 'bold' }}>User Management</div>
          <div style={{ fontSize: 11, color: T.dim, marginTop: 2 }}>{profiles.length} accounts</div>
        </div>
        <Btn variant="primary" onClick={openAdd}>+ Add User</Btn>
      </div>

      {profiles.map((p, i) => (
        <Card key={p.id} style={{ borderLeft: `3px solid ${p.role === 'admin' ? T.yellow : T.teal}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                <div style={{ width: 34, height: 34, borderRadius: '50%', background: `linear-gradient(135deg,${ACCENTS[i % ACCENTS.length]},${ACCENTS[(i + 3) % ACCENTS.length]})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 'bold', color: '#fff', flexShrink: 0 }}>
                  {p.name.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 'bold', color: '#f0f4f8' }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: T.teal }}>{p.email}</div>
                </div>
                <Badge color={p.role === 'admin' ? T.yellow : T.teal}>{p.role === 'admin' ? 'Admin' : 'Staff'}</Badge>
                {!p.active && <Badge color={T.pink}>Inactive</Badge>}
              </div>
              <div style={{ fontSize: 11, color: T.dim, marginLeft: 44, lineHeight: 1.7 }}>
                💰 ${p.hourly_rate}/hr &nbsp;·&nbsp; 👥 {p.clients?.length || 0} clients: {p.clients?.slice(0, 3).map(c => c.client_name).join(', ')}{(p.clients?.length || 0) > 3 ? ` +${p.clients.length - 3} more` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 7 }}>
              <Btn variant="ghost" onClick={() => openEdit(p)} style={{ padding: '7px 13px', fontSize: 11 }}>✏ Edit</Btn>
              <Btn variant={p.active ? 'danger' : 'success'} onClick={() => toggleActive(p)} style={{ padding: '7px 13px', fontSize: 11 }}>{p.active ? 'Deactivate' : 'Activate'}</Btn>
            </div>
          </div>
        </Card>
      ))}

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === 'add' ? 'Add New User' : `Edit — ${modal?.name}`}>
        {formErr && <div style={{ background: 'rgba(233,30,140,0.1)', border: `1px solid rgba(233,30,140,0.28)`, borderRadius: 8, padding: '9px 12px', marginBottom: 14, fontSize: 12, color: '#ff6eb0' }}>⚠ {formErr}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div><Lbl>Full Name</Lbl><Inp value={form.name || ''} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} /></div>
          <div><Lbl>Role</Lbl>
            <Sel value={form.role || 'staff'} onChange={e => setForm(p => ({ ...p, role: e.target.value }))}>
              <option value="staff" style={{ background: '#0d1f3c' }}>Staff</option>
              <option value="admin" style={{ background: '#0d1f3c' }}>Admin</option>
            </Sel>
          </div>
        </div>
        <div style={{ marginBottom: 12 }}><Lbl>Email Address</Lbl><Inp type="email" value={form.email || ''} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div><Lbl>{modal === 'add' ? 'Password' : 'New Password (optional)'}</Lbl><Inp type="password" value={form.password || ''} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} placeholder={modal === 'add' ? 'Required' : 'Leave blank to keep'} /></div>
          <div><Lbl>Hourly Rate ($)</Lbl><Inp type="number" value={form.hourly_rate || 25} onChange={e => setForm(p => ({ ...p, hourly_rate: parseFloat(e.target.value) || 0 }))} min="0" step="0.25" /></div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <Lbl>Assigned Clients</Lbl>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, maxHeight: 200, overflowY: 'auto', padding: 12, background: 'rgba(255,255,255,0.04)', borderRadius: 10, border: `1px solid ${T.border}` }}>
            {clients.map(c => (
              <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', padding: '4px 6px', borderRadius: 6, background: (form.clients || []).includes(c.name) ? 'rgba(0,181,200,0.1)' : 'transparent' }}>
                <input type="checkbox" checked={(form.clients || []).includes(c.name)}
                  onChange={e => setForm(p => ({ ...p, clients: e.target.checked ? [...(p.clients || []), c.name] : (p.clients || []).filter(x => x !== c.name) }))}
                  style={{ accentColor: T.teal }} />
                <span style={{ fontSize: 12, color: (form.clients || []).includes(c.name) ? '#f0f4f8' : T.dim }}>{c.name}</span>
              </label>
            ))}
          </div>
          <div style={{ fontSize: 10, color: T.dim, marginTop: 5 }}>{(form.clients || []).length} selected</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
          <Btn variant="primary" onClick={save} loading={saving}>{modal === 'add' ? 'Create User' : 'Save Changes'}</Btn>
        </div>
      </Modal>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ADMIN — ALL SESSIONS + CORRECTIONS
// ─────────────────────────────────────────────────────────────────────────────
function AdminSessions({ sessions, profiles, onRefresh, showToast }) {
  const [filterUser, setFilterUser] = useState('all')
  const [corrModal, setCorrModal]   = useState(null)
  const [corrForm, setCorrForm]     = useState({})
  const [saving, setSaving]         = useState(false)

  const shown = filterUser === 'all' ? sessions : sessions.filter(s => s.user_id === filterUser)

  const openCorrect = s => { setCorrForm({ clockIn: toLocalDT(s.clock_in), clockOut: toLocalDT(s.clock_out) }); setCorrModal(s) }

  const saveCorrection = async () => {
    const ci = new Date(corrForm.clockIn), co = new Date(corrForm.clockOut)
    if (isNaN(ci) || isNaN(co)) { showToast('Invalid date/time', 'error'); return }
    if (co <= ci) { showToast('Clock out must be after clock in', 'error'); return }
    setSaving(true)
    const { error } = await supabase.from('sessions').update({
      clock_in: ci.toISOString(), clock_out: co.toISOString(),
      duration_ms: co - ci, corrected: true
    }).eq('id', corrModal.id)
    setSaving(false)
    if (error) { showToast(error.message, 'error'); return }
    showToast('Session corrected!'); setCorrModal(null); onRefresh()
  }

  const staffList = profiles.filter(p => p.role === 'staff')

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 'bold' }}>All Sessions</div>
          <div style={{ fontSize: 11, color: T.dim, marginTop: 2 }}>{shown.length} sessions</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Sel value={filterUser} onChange={e => setFilterUser(e.target.value)} style={{ width: 'auto', padding: '8px 32px 8px 12px' }}>
            <option value="all" style={{ background: '#0d1f3c' }}>All Staff</option>
            {staffList.map(p => <option key={p.id} value={p.id} style={{ background: '#0d1f3c' }}>{p.name}</option>)}
          </Sel>
          <Btn variant="ghost" onClick={() => dlCSV(sessToCSV(shown, profiles), 'HHH_All_Sessions.csv')} style={{ padding: '8px 14px', fontSize: 11 }}>⬇ Export CSV</Btn>
        </div>
      </div>

      {shown.length === 0
        ? <div style={{ textAlign: 'center', padding: 50, color: 'rgba(255,255,255,0.18)', fontSize: 13 }}>No sessions found</div>
        : shown.slice(0, 60).map(s => {
          const su = profiles.find(p => p.id === s.user_id)
          const dur = s.duration_ms || (s.clock_out ? new Date(s.clock_out) - new Date(s.clock_in) : 0)
          const canCorrect = within24h(s.clock_in)
          return (
            <div key={s.id} style={{ marginBottom: 10, padding: 16, background: T.card, borderRadius: 13, border: `1px solid ${T.border}`, borderLeft: `3px solid ${canCorrect ? T.teal : T.dteal}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 13, fontWeight: 'bold' }}>{s.client_name}</div>
                    <div style={{ fontSize: 11, color: T.teal }}>{s.service}</div>
                    {s.corrected && <Badge color={T.orange}>Corrected</Badge>}
                  </div>
                  <div style={{ fontSize: 11, color: T.dim }}>👤 {su?.name || '—'} · 📅 {fmtDate(s.clock_in)} · 🕐 {fmtTime(s.clock_in)}–{fmtTime(s.clock_out)}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
                  <div style={{ padding: '3px 10px', background: 'rgba(0,200,100,0.1)', borderRadius: 9, fontSize: 12, color: T.green, fontFamily: 'monospace', fontWeight: 'bold', border: `1px solid rgba(0,200,100,0.2)` }}>{fmtDur(dur)}</div>
                  {canCorrect
                    ? <Btn variant="ghost" onClick={() => openCorrect(s)} style={{ padding: '5px 11px', fontSize: 10, color: T.yellow, border: `1px solid ${T.yellow}40` }}>✏ Correct</Btn>
                    : <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.2)' }}>Locked</div>}
                </div>
              </div>
              {s.notes && <div style={{ marginTop: 8, padding: '6px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: 7, fontSize: 11, color: 'rgba(255,255,255,0.38)', fontStyle: 'italic' }}>"{s.notes}"</div>}
            </div>
          )
        })
      }

      <Modal open={!!corrModal} onClose={() => setCorrModal(null)} title="Correct Session Time" width="420px">
        {corrModal && (<>
          <div style={{ background: `rgba(255,215,0,0.08)`, border: `1px solid ${T.yellow}30`, borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
            <b style={{ color: T.yellow }}>{corrModal.client_name}</b> · {profiles.find(p => p.id === corrModal.user_id)?.name}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div><Lbl>Clock In</Lbl><Inp type="datetime-local" value={corrForm.clockIn} onChange={e => setCorrForm(p => ({ ...p, clockIn: e.target.value }))} style={{ colorScheme: 'dark' }} /></div>
            <div><Lbl>Clock Out</Lbl><Inp type="datetime-local" value={corrForm.clockOut} onChange={e => setCorrForm(p => ({ ...p, clockOut: e.target.value }))} style={{ colorScheme: 'dark' }} /></div>
          </div>
          {corrForm.clockIn && corrForm.clockOut && new Date(corrForm.clockOut) > new Date(corrForm.clockIn) && (
            <div style={{ fontSize: 12, color: T.green, marginBottom: 12 }}>✓ New duration: {fmtDur(new Date(corrForm.clockOut) - new Date(corrForm.clockIn))}</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Btn variant="ghost" onClick={() => setCorrModal(null)}>Cancel</Btn>
            <Btn variant="yellow" onClick={saveCorrection} loading={saving}>Save Correction</Btn>
          </div>
        </>)}
      </Modal>
    </div>
  )
}


// ─────────────────────────────────────────────────────────────────────────────
// ADMIN — CLIENT MANAGEMENT
// ─────────────────────────────────────────────────────────────────────────────
function AdminClients({ clients, onRefresh, showToast }) {
  const [modal, setModal]       = useState(null) // null | 'add' | client-obj
  const [name, setName]         = useState('')
  const [formErr, setFormErr]   = useState('')
  const [saving, setSaving]     = useState(false)
  const [search, setSearch]     = useState('')

  const openAdd  = () => { setName(''); setFormErr(''); setModal('add') }
  const openEdit = c  => { setName(c.name); setFormErr(''); setModal(c) }

  const save = async () => {
    if (!name.trim()) { setFormErr('Client name is required.'); return }
    const conflict = clients.find(c => c.name.toLowerCase() === name.trim().toLowerCase() && c.id !== modal?.id)
    if (conflict) { setFormErr('A client with this name already exists.'); return }
    setSaving(true)
    if (modal === 'add') {
      const { error } = await supabase.from('clients').insert({ name: name.trim(), active: true })
      if (error) { setFormErr(error.message); setSaving(false); return }
      showToast('Client added!')
    } else {
      const { error } = await supabase.from('clients').update({ name: name.trim() }).eq('id', modal.id)
      if (error) { setFormErr(error.message); setSaving(false); return }
      showToast('Client updated!')
    }
    setSaving(false); setModal(null); onRefresh()
  }

  const toggleActive = async c => {
    await supabase.from('clients').update({ active: !c.active }).eq('id', c.id)
    showToast(c.active ? 'Client deactivated' : 'Client reactivated')
    onRefresh()
  }

  const deleteClient = async c => {
    if (!window.confirm(`Delete ${c.name}? This cannot be undone.`)) return
    await supabase.from('clients').delete().eq('id', c.id)
    showToast('Client deleted')
    onRefresh()
  }

  const filtered = clients.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
  const active   = filtered.filter(c => c.active)
  const inactive = filtered.filter(c => !c.active)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 'bold' }}>Client Management</div>
          <div style={{ fontSize: 11, color: T.dim, marginTop: 2 }}>{clients.filter(c=>c.active).length} active · {clients.filter(c=>!c.active).length} inactive</div>
        </div>
        <Btn variant="primary" onClick={openAdd}>+ Add Client</Btn>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16, position: 'relative' }}>
        <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: T.dim, fontSize: 14 }}>🔍</span>
        <Inp value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..." style={{ paddingLeft: 36 }} />
      </div>

      {/* Active clients */}
      {active.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: T.teal, marginBottom: 10, textTransform: 'uppercase', fontWeight: 'bold' }}>Active Clients ({active.length})</div>
          {active.map((c, i) => (
            <div key={c.id} style={{ marginBottom: 8, padding: '14px 16px', background: T.card, borderRadius: 12, border: `1px solid ${T.border}`, borderLeft: `3px solid ${ACCENTS[i % ACCENTS.length]}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: `linear-gradient(135deg,${ACCENTS[i % ACCENTS.length]},${ACCENTS[(i+3) % ACCENTS.length]})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 'bold', color: '#fff', flexShrink: 0 }}>
                  {c.name.split(' ').map(p => p[0]).join('').slice(0,2).toUpperCase()}
                </div>
                <div style={{ fontSize: 14, fontWeight: 'bold', color: '#f0f4f8' }}>{c.name}</div>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn variant="ghost" onClick={() => openEdit(c)} style={{ padding: '6px 12px', fontSize: 11 }}>✏ Edit</Btn>
                <Btn variant="danger" onClick={() => toggleActive(c)} style={{ padding: '6px 12px', fontSize: 11 }}>Deactivate</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Inactive clients */}
      {inactive.length > 0 && (
        <div>
          <div style={{ fontSize: 10, letterSpacing: 2, color: T.dim, marginBottom: 10, textTransform: 'uppercase', fontWeight: 'bold' }}>Inactive Clients ({inactive.length})</div>
          {inactive.map(c => (
            <div key={c.id} style={{ marginBottom: 8, padding: '14px 16px', background: 'rgba(255,255,255,0.02)', borderRadius: 12, border: `1px solid rgba(255,255,255,0.05)`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, opacity: 0.6 }}>
              <div style={{ fontSize: 14, color: T.dim }}>{c.name}</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <Btn variant="success" onClick={() => toggleActive(c)} style={{ padding: '6px 12px', fontSize: 11 }}>Reactivate</Btn>
                <Btn variant="danger" onClick={() => deleteClient(c)} style={{ padding: '6px 12px', fontSize: 11, background: 'rgba(233,30,140,0.15)', boxShadow: 'none' }}>🗑 Delete</Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 50, color: 'rgba(255,255,255,0.18)', fontSize: 13 }}>
          {search ? 'No clients match your search' : 'No clients yet — add your first one!'}
        </div>
      )}

      <Modal open={!!modal} onClose={() => setModal(null)} title={modal === 'add' ? 'Add New Client' : `Edit — ${modal?.name}`} width="400px">
        {formErr && <div style={{ background: 'rgba(233,30,140,0.1)', border: `1px solid rgba(233,30,140,0.28)`, borderRadius: 8, padding: '9px 12px', marginBottom: 14, fontSize: 12, color: '#ff6eb0' }}>⚠ {formErr}</div>}
        <div style={{ marginBottom: 20 }}>
          <Lbl>Client Full Name</Lbl>
          <Inp value={name} onChange={e => { setName(e.target.value); setFormErr('') }} placeholder="First and last name" onKeyDown={e => e.key === 'Enter' && save()} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
          <Btn variant="primary" onClick={save} loading={saving}>{modal === 'add' ? 'Add Client' : 'Save Changes'}</Btn>
        </div>
      </Modal>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// REPORTS (shared by admin + staff, admin sees all)
// ─────────────────────────────────────────────────────────────────────────────
function Reports({ sessions, profiles, currentProfile, isAdmin }) {
  const [selUser,  setSelUser]  = useState(isAdmin ? 'all' : currentProfile.id)
  const [selMonth, setSelMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}` })
  const [weekOff,  setWeekOff]  = useState(0)

  const wRef = useMemo(() => { const d = new Date(); d.setDate(d.getDate() + weekOff * 7); return d }, [weekOff])
  const wS   = useMemo(() => wkStart(wRef), [wRef])
  const wE   = useMemo(() => wkEnd(wRef), [wRef])
  const [sY, sMo] = useMemo(() => [parseInt(selMonth.split('-')[0]), parseInt(selMonth.split('-')[1]) - 1], [selMonth])

  const base   = useMemo(() => selUser === 'all' ? sessions : sessions.filter(s => s.user_id === selUser), [sessions, selUser])
  const wkSess = useMemo(() => base.filter(s => new Date(s.clock_in) >= wS && new Date(s.clock_in) <= wE), [base, wS, wE])
  const moSess = useMemo(() => base.filter(s => new Date(s.clock_in).getFullYear() === sY && new Date(s.clock_in).getMonth() === sMo), [base, sY, sMo])
  const wkTot  = useMemo(() => wkSess.reduce((a, s) => a + (s.duration_ms || 0), 0), [wkSess])
  const moTot  = useMemo(() => moSess.reduce((a, s) => a + (s.duration_ms || 0), 0), [moSess])

  const selProf = profiles.find(p => p.id === selUser)
  const rate    = selProf?.hourly_rate || currentProfile.hourly_rate || 25
  const regH    = Math.min(msToH(wkTot), 40)
  const otH     = Math.max(0, msToH(wkTot) - 40)
  const gross   = regH * rate + otH * rate * 1.5

  const staffList = profiles.filter(p => p.role === 'staff')

  // Per-staff monthly table (admin only)
  const staffMonthly = useMemo(() => staffList.map(u => {
    const uS  = sessions.filter(s => s.user_id === u.id && new Date(s.clock_in).getFullYear() === sY && new Date(s.clock_in).getMonth() === sMo)
    const tot = uS.reduce((a, s) => a + (s.duration_ms || 0), 0)
    const rH  = Math.min(msToH(tot), 40), oH = Math.max(0, msToH(tot) - 40)
    return { ...u, uSessions: uS, total: tot, gross: rH * u.hourly_rate + oH * u.hourly_rate * 1.5 }
  }), [staffList, sessions, sY, sMo])

  const exportPayroll = () => {
    const rows = [['HAPPY HEARTS HAVEN – PAYROLL EXPORT'], ['Month', selMonth], [''], ['Staff', 'Total Hours', 'Regular', 'Overtime', 'Rate', 'Regular Pay', 'OT Pay', 'Gross'],
      ...staffMonthly.map(u => { const rH = Math.min(msToH(u.total), 40), oH = Math.max(0, msToH(u.total) - 40); return [u.name, fmtH(u.total), rH.toFixed(2), oH.toFixed(2), fmtMoney(u.hourly_rate), fmtMoney(rH*u.hourly_rate), fmtMoney(oH*u.hourly_rate*1.5), fmtMoney(u.gross)] })]
    dlCSV(rows.map(r => r.join(',')).join('\n'), `HHH_Payroll_${selMonth}.csv`)
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        {isAdmin && (
          <Sel value={selUser} onChange={e => setSelUser(e.target.value)} style={{ width: 'auto', padding: '9px 32px 9px 13px' }}>
            <option value="all" style={{ background: '#0d1f3c' }}>All Staff</option>
            {staffList.map(p => <option key={p.id} value={p.id} style={{ background: '#0d1f3c' }}>{p.name}</option>)}
          </Sel>
        )}
        <Inp type="month" value={selMonth} onChange={e => setSelMonth(e.target.value)} style={{ width: 160, colorScheme: 'dark' }} />
        {isAdmin && <Btn variant="yellow" onClick={exportPayroll} style={{ padding: '9px 16px' }}>⬇ Payroll CSV</Btn>}
        <Btn variant="ghost" onClick={() => dlCSV(sessToCSV(moSess, profiles), `HHH_${selMonth}.csv`)} style={{ padding: '9px 14px', fontSize: 11 }}>⬇ Sessions CSV</Btn>
      </div>

      {/* Summary stats */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
        <Stat label="Month Hours"    value={`${fmtH(moTot)}h`}  icon="📆" accent={T.teal} />
        <Stat label="Month Sessions" value={moSess.length}       icon="📋" accent={T.pink} />
        <Stat label="Week Hours"     value={`${fmtH(wkTot)}h`}  icon="📅" accent={T.yellow} />
        <Stat label="Est. Gross Pay" value={fmtMoney(gross)}     icon="💰" accent={T.green} />
      </div>

      {/* Payroll table (admin) */}
      {isAdmin && (
        <Card>
          <SH>Monthly Payroll — {selMonth}</SH>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                  {['Staff', 'Hours', 'Regular', 'Overtime', 'Rate', 'Gross'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: 'left', color: T.dim, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', fontWeight: 'normal' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {staffMonthly.map(u => {
                  const rH = Math.min(msToH(u.total), 40), oH = Math.max(0, msToH(u.total) - 40)
                  return (
                    <tr key={u.id} style={{ borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
                      <td style={{ padding: '9px 10px' }}><div style={{ fontWeight: 'bold' }}>{u.name}</div><div style={{ fontSize: 10, color: T.dim }}>{u.uSessions.length} sess.</div></td>
                      <td style={{ padding: '9px 10px', fontFamily: 'monospace', color: T.teal }}>{fmtH(u.total)}h</td>
                      <td style={{ padding: '9px 10px', fontFamily: 'monospace', color: 'rgba(255,255,255,0.6)' }}>{rH.toFixed(2)}h</td>
                      <td style={{ padding: '9px 10px', fontFamily: 'monospace', color: oH > 0 ? T.orange : 'rgba(255,255,255,0.3)' }}>{oH.toFixed(2)}h</td>
                      <td style={{ padding: '9px 10px', color: T.dim }}>{fmtMoney(u.hourly_rate)}</td>
                      <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 'bold', color: T.yellow }}>{fmtMoney(u.gross)}</td>
                    </tr>
                  )
                })}
                <tr style={{ borderTop: `2px solid ${T.border}` }}>
                  <td style={{ padding: '9px 10px', fontWeight: 'bold' }}>TOTAL</td>
                  <td style={{ padding: '9px 10px', fontFamily: 'monospace', color: T.teal, fontWeight: 'bold' }}>{fmtH(staffMonthly.reduce((a, u) => a + u.total, 0))}h</td>
                  <td colSpan={3} />
                  <td style={{ padding: '9px 10px', fontFamily: 'monospace', fontWeight: 'bold', color: T.yellow, fontSize: 15 }}>{fmtMoney(staffMonthly.reduce((a, u) => a + u.gross, 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Weekly bar chart */}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <SH style={{ margin: 0 }}>Weekly Hours</SH>
          <div style={{ display: 'flex', gap: 6 }}>
            <Btn variant="ghost" onClick={() => setWeekOff(o => o - 1)} style={{ padding: '5px 11px', fontSize: 11 }}>‹</Btn>
            <span style={{ fontSize: 11, color: T.dim, alignSelf: 'center' }}>{fmtDate(wS)} – {fmtDate(wE)}</span>
            <Btn variant="ghost" onClick={() => setWeekOff(o => Math.min(o + 1, 0))} style={{ padding: '5px 11px', fontSize: 11, opacity: weekOff >= 0 ? 0.3 : 1 }}>›</Btn>
          </div>
        </div>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((day, i) => {
          const d  = new Date(wS); d.setDate(d.getDate() + i)
          const ds = wkSess.filter(s => new Date(s.clock_in).toDateString() === d.toDateString())
          const hrs = msToH(ds.reduce((a, s) => a + (s.duration_ms || 0), 0))
          return (
            <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
              <div style={{ width: 30, fontSize: 10, color: T.dim, textAlign: 'right' }}>{day}</div>
              <div style={{ flex: 1, height: 20, background: 'rgba(255,255,255,0.05)', borderRadius: 5, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(hrs / 10 * 100, 100)}%`, background: ds.length ? `linear-gradient(90deg,${T.teal},${T.dteal})` : 'transparent', borderRadius: 5, display: 'flex', alignItems: 'center', paddingLeft: 7, minWidth: ds.length ? 28 : 0 }}>
                  {ds.length > 0 && <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)' }}>{hrs.toFixed(1)}h</span>}
                </div>
              </div>
              <div style={{ width: 45, fontSize: 10, color: 'rgba(255,255,255,0.25)', textAlign: 'right' }}>{ds.length} sess.</div>
            </div>
          )
        })}
      </Card>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// STAFF CLOCK
// ─────────────────────────────────────────────────────────────────────────────
function StaffClock({ currentProfile, sessions, onSessionSaved, showToast }) {
  const [clockedIn, setClockedIn]     = useState(false)
  const [clockInTime, setClockInTime] = useState(null)
  const [clockOutTime, setClockOutTime] = useState(null) // eslint-disable-line no-unused-vars
  const [elapsed, setElapsed]         = useState(0)
  const [now, setNow]                 = useState(new Date())
  const [location, setLocation]       = useState(null)
  const [locErr, setLocErr]           = useState(null)
  const [locLoading, setLocLoading]   = useState(false)
  const [client, setClient]           = useState('')
  const [service, setService]         = useState('')
  const [notes, setNotes]             = useState('')
  const [formErr, setFormErr]         = useState('')
  const [saving, setSaving]           = useState(false)
  const [staffTab, setStaffTab]       = useState('clock')
  const [corrModal, setCorrModal]     = useState(null)
  const [corrForm, setCorrForm]       = useState({})
  const [corrSaving, setCorrSaving]   = useState(false)
  const timer = useRef(null)

  const mySessions = useMemo(() => sessions.filter(s => s.user_id === currentProfile.id), [sessions, currentProfile.id])

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t) }, [])
  useEffect(() => {
    if (clockedIn && clockInTime) timer.current = setInterval(() => setElapsed(Date.now() - clockInTime.getTime()), 1000)
    else clearInterval(timer.current)
    return () => clearInterval(timer.current)
  }, [clockedIn, clockInTime])

  const getLoc = () => new Promise(res => {
    if (!navigator.geolocation) { res(null); return }
    setLocLoading(true)
    navigator.geolocation.getCurrentPosition(
      p => { setLocLoading(false); const l = { lat: p.coords.latitude.toFixed(5), lng: p.coords.longitude.toFixed(5), accuracy: Math.round(p.coords.accuracy) }; setLocation(l); res(l) },
      () => { setLocLoading(false); setLocErr('Location unavailable'); res(null) },
      { enableHighAccuracy: true, timeout: 8000 }
    )
  })

  const clockIn = async () => {
    if (!client)  { setFormErr('Select a client.');  return }
    if (!service) { setFormErr('Select a service.'); return }
    setFormErr(''); await getLoc()
    const t = new Date(); setClockInTime(t); setClockOutTime(null); setElapsed(0); setClockedIn(true)
  }

  const clockOut = async () => {
    const loc = await getLoc(), t = new Date()
    setClockOutTime(t); setClockedIn(false); setSaving(true)
    const dur = t - clockInTime
    const { error } = await supabase.from('sessions').insert({
      user_id: currentProfile.id, client_name: client, service, notes,
      clock_in: clockInTime.toISOString(), clock_out: t.toISOString(), duration_ms: dur,
      lat: loc?.lat || null, lng: loc?.lng || null, accuracy: loc?.accuracy || null,
      corrected: false,
    })
    setSaving(false)
    if (error) { showToast(error.message, 'error'); return }
    showToast('Session saved!'); setNotes(''); onSessionSaved()
  }

  const openSelfCorrect = s => { setCorrForm({ clockIn: toLocalDT(s.clock_in), clockOut: toLocalDT(s.clock_out) }); setCorrModal(s) }
  const saveSelfCorrect = async () => {
    const ci = new Date(corrForm.clockIn), co = new Date(corrForm.clockOut)
    if (isNaN(ci) || isNaN(co)) { showToast('Invalid date/time', 'error'); return }
    if (co <= ci) { showToast('Clock out must be after clock in', 'error'); return }
    setCorrSaving(true)
    await supabase.from('sessions').update({ clock_in: ci.toISOString(), clock_out: co.toISOString(), duration_ms: co - ci, corrected: true }).eq('id', corrModal.id)
    setCorrSaving(false); showToast('Corrected!'); setCorrModal(null); onSessionSaved()
  }

  const liveTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  const liveDate = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
  const tabSt    = act => ({ padding: '8px 15px', borderRadius: 8, border: 'none', fontSize: 11, cursor: 'pointer', fontFamily: 'Georgia,serif', background: act ? 'rgba(0,181,200,0.18)' : 'rgba(255,255,255,0.04)', color: act ? T.teal : T.dim, borderBottom: act ? `2px solid ${T.teal}` : '2px solid transparent' })

  return (
    <div>
      <div style={{ display: 'flex', gap: 5, marginBottom: 20 }}>
        {[['clock','⏱ Clock'], ['history','📋 My Sessions'], ['correct','✏ Corrections']].map(([id, lbl]) => (
          <button key={id} onClick={() => setStaffTab(id)} style={tabSt(staffTab === id)}>{lbl}</button>
        ))}
      </div>

      {staffTab === 'clock' && (
        <div>
          <Card style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 50, fontWeight: 'bold', fontFamily: "'Courier New',monospace", background: `linear-gradient(135deg,${T.teal},#fff)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1, letterSpacing: 2 }}>{liveTime}</div>
            <div style={{ color: 'rgba(255,255,255,0.38)', fontSize: 12, marginTop: 6 }}>{liveDate}</div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, marginTop: 12, padding: '5px 14px', borderRadius: 20, background: clockedIn ? 'rgba(0,200,100,0.1)' : 'rgba(255,255,255,0.05)', border: clockedIn ? `1px solid rgba(0,200,100,0.22)` : `1px solid ${T.border}` }}>
              <div style={{ width: 6, height: 6, borderRadius: '50%', background: clockedIn ? T.green : '#555', boxShadow: clockedIn ? `0 0 8px ${T.green}` : 'none' }} />
              <span style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: clockedIn ? T.green : 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>{clockedIn ? 'Active Session' : 'Not Clocked In'}</span>
            </div>
          </Card>

          {clockedIn && (
            <Card style={{ textAlign: 'center', background: 'rgba(0,200,100,0.04)', border: `1px solid rgba(0,200,100,0.14)` }}>
              <div style={{ fontSize: 9, letterSpacing: 2, color: T.dim, textTransform: 'uppercase', marginBottom: 5 }}>Session Duration</div>
              <div style={{ fontSize: 38, fontFamily: 'monospace', fontWeight: 'bold', color: T.green, letterSpacing: 3 }}>{fmtDur(elapsed)}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', marginTop: 5 }}>Started {fmtDT(clockInTime)}</div>
            </Card>
          )}

          <Card>
            <SH>Session Details</SH>
            {formErr && <div style={{ background: 'rgba(233,30,140,0.1)', border: `1px solid rgba(233,30,140,0.28)`, borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#ff6eb0' }}>⚠ {formErr}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <Lbl>Client *</Lbl>
                <Sel value={client} onChange={e => { setClient(e.target.value); setFormErr('') }} dis={clockedIn}>
                  <option value="" style={{ background: '#0d1f3c' }}>Select client…</option>
                  {(currentProfile.clients || []).map(c => <option key={c.client_name} value={c.client_name} style={{ background: '#0d1f3c' }}>{c.client_name}</option>)}
                </Sel>
              </div>
              <div>
                <Lbl>Service *</Lbl>
                <Sel value={service} onChange={e => { setService(e.target.value); setFormErr('') }} dis={clockedIn}>
                  <option value="" style={{ background: '#0d1f3c' }}>Select service…</option>
                  {SERVICES.map(s => <option key={s} value={s} style={{ background: '#0d1f3c' }}>{s}</option>)}
                </Sel>
              </div>
            </div>
            <Lbl>Notes (optional)</Lbl>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Session notes…" rows={2} style={{ width: '100%', padding: '10px 13px', borderRadius: 10, background: 'rgba(255,255,255,0.07)', border: `1px solid ${T.border}`, color: '#f0f4f8', fontSize: 13, outline: 'none', fontFamily: 'Georgia,serif', resize: 'vertical', boxSizing: 'border-box' }} />
          </Card>

          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 16, padding: '10px 13px', background: 'rgba(255,255,255,0.02)', borderRadius: 10, border: `1px solid rgba(255,255,255,0.05)` }}>
            <span>📍</span>
            <span style={{ fontSize: 11, color: locLoading ? T.teal : location ? 'rgba(255,255,255,0.4)' : locErr ? '#ff6eb0' : 'rgba(255,255,255,0.22)' }}>
              {locLoading ? 'Acquiring location…' : location ? `${location.lat}°N, ${location.lng}°W — ±${location.accuracy}m` : locErr || 'Location captured on clock in/out'}
            </span>
          </div>

          {!clockedIn
            ? <Btn variant="primary" onClick={clockIn} loading={saving} style={{ width: '100%', justifyContent: 'center', padding: 15, fontSize: 14, letterSpacing: '1.5px', textTransform: 'uppercase' }}>▶ Clock In</Btn>
            : <Btn variant="danger"  onClick={clockOut} loading={saving} style={{ width: '100%', justifyContent: 'center', padding: 15, fontSize: 14, letterSpacing: '1.5px', textTransform: 'uppercase' }}>■ Clock Out</Btn>
          }
        </div>
      )}

      {staffTab === 'history' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 'bold' }}>My Sessions</div>
              <div style={{ fontSize: 11, color: T.dim, marginTop: 2 }}>{mySessions.length} sessions</div>
            </div>
            <Btn variant="ghost" onClick={() => dlCSV(sessToCSV(mySessions, [currentProfile]), 'My_Sessions.csv')} style={{ padding: '8px 14px', fontSize: 11 }}>⬇ Export</Btn>
          </div>
          {mySessions.length === 0
            ? <div style={{ textAlign: 'center', padding: 50, color: 'rgba(255,255,255,0.18)' }}>No sessions yet</div>
            : mySessions.map(s => {
              const dur = s.duration_ms || (s.clock_out ? new Date(s.clock_out) - new Date(s.clock_in) : 0)
              return (
                <div key={s.id} style={{ marginBottom: 10, padding: 15, background: T.card, borderRadius: 13, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.teal}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div><div style={{ fontSize: 13, fontWeight: 'bold' }}>{s.client_name}</div><div style={{ fontSize: 11, color: T.teal, marginTop: 2 }}>{s.service}</div></div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                      <div style={{ padding: '3px 10px', background: 'rgba(0,200,100,0.1)', borderRadius: 9, fontSize: 12, color: T.green, fontFamily: 'monospace', fontWeight: 'bold', border: `1px solid rgba(0,200,100,0.2)` }}>{fmtDur(dur)}</div>
                      {s.corrected && <Badge color={T.orange}>Corrected</Badge>}
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, fontSize: 11, color: T.dim, marginTop: 8 }}>
                    <div>📅 {fmtDate(s.clock_in)}</div><div>📍 {s.lat ? `${s.lat}°N` : '—'}</div>
                    <div>🕐 In: {fmtTime(s.clock_in)}</div><div>🕐 Out: {fmtTime(s.clock_out)}</div>
                  </div>
                </div>
              )
            })
          }
        </div>
      )}

      {staffTab === 'correct' && (
        <div>
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 18, fontWeight: 'bold' }}>Time Corrections</div>
            <div style={{ fontSize: 11, color: T.dim, marginTop: 2 }}>Correct sessions within 24 hours</div>
          </div>
          <div style={{ background: `rgba(255,215,0,0.06)`, border: `1px solid ${T.yellow}25`, borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
            ⚠ Only sessions from the last 24 hours are editable. Contact your admin for older corrections.
          </div>
          {mySessions.filter(s => within24h(s.clock_in)).length === 0
            ? <div style={{ textAlign: 'center', padding: 40, color: 'rgba(255,255,255,0.18)', fontSize: 13 }}>No sessions in the last 24 hours</div>
            : mySessions.filter(s => within24h(s.clock_in)).map(s => {
              const dur = s.duration_ms || 0
              return (
                <div key={s.id} style={{ marginBottom: 10, padding: 15, background: T.card, borderRadius: 13, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.yellow}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 'bold' }}>{s.client_name} · <span style={{ color: T.teal, fontSize: 12 }}>{s.service}</span></div>
                      <div style={{ fontSize: 11, color: T.dim, marginTop: 3 }}>In: {fmtDT(s.clock_in)} · Out: {fmtDT(s.clock_out)} · {fmtDur(dur)}</div>
                    </div>
                    <Btn variant="ghost" onClick={() => openSelfCorrect(s)} style={{ padding: '7px 13px', fontSize: 11, color: T.yellow, border: `1px solid ${T.yellow}40` }}>✏ Edit</Btn>
                  </div>
                </div>
              )
            })
          }
        </div>
      )}

      <Modal open={!!corrModal} onClose={() => setCorrModal(null)} title="Correct My Session" width="420px">
        {corrModal && (<>
          <div style={{ background: `rgba(255,215,0,0.08)`, border: `1px solid ${T.yellow}30`, borderRadius: 10, padding: 12, marginBottom: 16, fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
            <b style={{ color: T.yellow }}>{corrModal.client_name}</b> · {corrModal.service}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div><Lbl>Clock In</Lbl><Inp type="datetime-local" value={corrForm.clockIn} onChange={e => setCorrForm(p => ({ ...p, clockIn: e.target.value }))} style={{ colorScheme: 'dark' }} /></div>
            <div><Lbl>Clock Out</Lbl><Inp type="datetime-local" value={corrForm.clockOut} onChange={e => setCorrForm(p => ({ ...p, clockOut: e.target.value }))} style={{ colorScheme: 'dark' }} /></div>
          </div>
          {corrForm.clockIn && corrForm.clockOut && new Date(corrForm.clockOut) > new Date(corrForm.clockIn) && (
            <div style={{ fontSize: 12, color: T.green, marginBottom: 12 }}>✓ New duration: {fmtDur(new Date(corrForm.clockOut) - new Date(corrForm.clockIn))}</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Btn variant="ghost" onClick={() => setCorrModal(null)}>Cancel</Btn>
            <Btn variant="yellow" onClick={saveSelfCorrect} loading={corrSaving}>Save Correction</Btn>
          </div>
        </>)}
      </Modal>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT APP
// ─────────────────────────────────────────────────────────────────────────────
export default function App() {
  const [authUser,       setAuthUser]       = useState(null)
  const [currentProfile, setCurrentProfile] = useState(null)
  const [profiles,       setProfiles]       = useState([])
  const [sessions,       setSessions]       = useState([])
  const [clients,        setClients]        = useState([])
  const [mainView,       setMainView]       = useState('clock')
  const [toast,          setToast]          = useState({ msg: '', type: 'success' })
  const [loading,        setLoading]        = useState(true)

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type }); setTimeout(() => setToast({ msg: '', type: 'success' }), 3000)
  }, [])

  // Auth listener
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) setAuthUser(session.user)
      else setLoading(false)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthUser(session?.user || null)
      if (!session) { setCurrentProfile(null); setLoading(false) }
    })
    return () => subscription.unsubscribe()
  }, [])

  // Load profile when auth user changes
  useEffect(() => {
    if (!authUser) return
    loadProfile() // eslint-disable-line react-hooks/exhaustive-deps
  }, [authUser]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadProfile = async () => {
    setLoading(true)
    const { data: profile } = await supabase
      .from('profiles')
      .select('*, clients:client_assignments(client_name)')
      .eq('id', authUser.id)
      .single()

    if (!profile) { showToast('Profile not found', 'error'); setLoading(false); return }
    if (!profile.active) { await supabase.auth.signOut(); showToast('Account deactivated', 'error'); return }

    setCurrentProfile({ ...profile, email: authUser.email })
    setMainView(profile.role === 'admin' ? 'sessions' : 'clock')
    await Promise.all([loadAllProfiles(profile), loadSessions(profile), loadClients()])
    setLoading(false)
  }

  const loadAllProfiles = async (profile) => {
    if (profile.role !== 'admin') return
    const { data } = await supabase
      .from('profiles')
      .select('*, clients:client_assignments(client_name)')
      .order('name')
    if (data) setProfiles(data.map(p => ({ ...p })))
  }

  const loadSessions = async (profile) => {
    let query = supabase.from('sessions').select('*').order('clock_in', { ascending: false }).limit(500)
    if (profile.role !== 'admin') query = query.eq('user_id', authUser.id)
    const { data } = await query
    if (data) setSessions(data)
  }

  const loadClients = async () => {
    const { data } = await supabase.from('clients').select('*').eq('active', true).order('name')
    if (data) setClients(data)
  }

  const loadClientsPublic = useCallback(async () => { await loadClients() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAll = useCallback(async () => {
    if (!currentProfile) return
    await Promise.all([loadAllProfiles(currentProfile), loadSessions(currentProfile)]) // eslint-disable-line react-hooks/exhaustive-deps
  }, [currentProfile]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await supabase.auth.signOut()
    setAuthUser(null); setCurrentProfile(null); setProfiles([]); setSessions([])
  }

  // ── Render ──────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Georgia,serif', color: T.teal, fontSize: 18 }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>💛</div>
        <div>Loading…</div>
      </div>
    </div>
  )

  if (!authUser || !currentProfile) return <LoginScreen onLogin={u => setAuthUser(u)} />

  const isAdmin = currentProfile.role === 'admin'
  const NAV = isAdmin
    ? [['sessions','📋 Sessions'], ['users','👥 Users'], ['clients','👤 Clients'], ['clock','⏱ My Clock'], ['reports','📊 Reports']]
    : [['clock','⏱ Clock'], ['reports','📊 Reports']]

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(135deg,${T.bg} 0%,${T.bg2} 50%,${T.bg} 100%)`, fontFamily: 'Georgia,serif', color: '#f0f4f8' }}>
      <div style={{ position: 'fixed', top: -80, right: -80, width: 300, height: 300, borderRadius: '50%', background: 'radial-gradient(circle,rgba(0,181,200,0.07) 0%,transparent 70%)', pointerEvents: 'none' }} />

      <Toast msg={toast.msg} type={toast.type} />

      {/* Header */}
      <div style={{ background: 'rgba(255,255,255,0.025)', backdropFilter: 'blur(16px)', borderBottom: `1px solid rgba(0,181,200,0.16)`, padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, background: `linear-gradient(135deg,${T.teal},${T.pink})`, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, boxShadow: `0 4px 12px rgba(0,181,200,0.28)` }}>💛</div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 'bold', color: T.teal }}>HappyHearts Haven</div>
            <div style={{ fontSize: 9, color: T.dim, letterSpacing: '1.5px', textTransform: 'uppercase' }}>Staff Time Tracker</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
          {NAV.map(([id, lbl]) => (
            <button key={id} onClick={() => setMainView(id)} style={{ padding: '6px 14px', borderRadius: 18, border: 'none', fontFamily: 'Georgia,serif', fontSize: 10, cursor: 'pointer', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 'bold', background: mainView === id ? `linear-gradient(135deg,${T.teal},${T.dteal})` : 'rgba(255,255,255,0.05)', color: mainView === id ? '#fff' : T.dim, boxShadow: mainView === id ? `0 4px 12px rgba(0,181,200,0.22)` : 'none' }}>{lbl}</button>
          ))}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 6, paddingLeft: 10, borderLeft: `1px solid ${T.border}` }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, fontWeight: 'bold', color: '#f0f4f8' }}>{currentProfile.name}</div>
              <div style={{ fontSize: 9, color: T.dim }}>{isAdmin ? 'Administrator' : 'Staff'}</div>
            </div>
            <button onClick={handleLogout} style={{ padding: '6px 11px', borderRadius: 8, background: 'rgba(233,30,140,0.12)', border: `1px solid rgba(233,30,140,0.25)`, color: T.pink, cursor: 'pointer', fontSize: 10, fontFamily: 'Georgia,serif', fontWeight: 'bold' }}>Sign Out</button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px' }}>
        {mainView === 'users'    && isAdmin && <AdminUsers    profiles={profiles} clients={clients}   onRefresh={refreshAll} showToast={showToast} />}
        {mainView === 'clients'  && isAdmin && <AdminClients  clients={clients} onRefresh={loadClientsPublic} showToast={showToast} />}
        {mainView === 'sessions' && isAdmin && <AdminSessions sessions={sessions} profiles={profiles} onRefresh={refreshAll} showToast={showToast} />}
        {mainView === 'clock'               && <StaffClock    currentProfile={currentProfile} sessions={sessions} onSessionSaved={refreshAll} showToast={showToast} />}
        {mainView === 'reports'             && <Reports sessions={sessions} profiles={profiles.length ? profiles : [currentProfile]} currentProfile={currentProfile} isAdmin={isAdmin} />}
      </div>

      <style>{`
        @media print { body{background:white!important;color:black!important} button{display:none!important} }
        select option { background:#0d1f3c; color:#f0f4f8 }
        ::-webkit-scrollbar{width:4px} ::-webkit-scrollbar-track{background:transparent} ::-webkit-scrollbar-thumb{background:rgba(0,181,200,0.3);border-radius:2px}
        input[type=month]::-webkit-calendar-picker-indicator,
        input[type=datetime-local]::-webkit-calendar-picker-indicator { filter:invert(0.5); cursor:pointer }
        input[type=number]{-moz-appearance:textfield} input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none}
        input::placeholder,textarea::placeholder{color:rgba(255,255,255,0.22)}
      `}</style>
    </div>
  )
}
